/**
 * fetchRequest - A utility function for making HTTP requests with callback support
 * 
 * This function provides a simple interface for making fetch requests with support for
 * form data, various HTTP methods, and callback functions for handling different states
 * of the request lifecycle.
 * 
 * @example
 * // Basic POST request with form data
 * const formData = new FormData();
 * formData.append('name', 'John Doe');
 * formData.append('email', 'john@example.com');
 * 
 * await fetchRequest({
 *   action: 'submit_contact_form',
 *   url: '/wp-admin/admin-ajax.php',
 *   formData: formData,
 *   onRequestStart: () => console.log('Request starting...'),
 *   onSuccess: (data) => console.log('Success:', data),
 *   onError: (error) => console.error('Error:', error),
 *   onFinally: () => console.log('Request completed')
 * });
 * 
 * @example
 * // GET request without form data
 * await fetchRequest({
 *   method: 'GET',
 *   url: '/api/users',
 *   onSuccess: (data) => displayUsers(data),
 *   onError: (error) => showErrorMessage(error.message)
 * });
 * 
 * @param {Object} config - Configuration object for the request
 * @param {string} [config.action] - WordPress action name (auto-appended to formData for POST requests)
 * @param {string} [config.method="POST"] - HTTP method (GET, POST, PUT, PATCH, DELETE)
 * @param {FormData|null} [config.formData=null] - Form data to send with the request
 * @param {string} config.url - The URL to make the request to
 * @param {Function} [config.onSuccess] - Callback function called on successful response
 * @param {Function} [config.onError] - Callback function called on error
 * @param {Function} [config.onFinally] - Callback function called after request completes (success or error)
 * @param {Function} [config.onRequestStart] - Callback function called before request starts
 * 
 * @returns {Promise<void>} Promise that resolves when the request completes
 */
window.fetchRequest = async function({
	action,
	method = "POST",
	formData = null,
	url,
	onSuccess,
	onError,
	onFinally,
	onRequestStart,
}) {
	// Auto-append WordPress action to form data for POST requests
	if (method === 'POST' && formData && action) {
		formData.append('action', action);
	}
	
	const options = { method };
	const postMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
	
	// Add form data as request body for methods that support it
	if (postMethods.includes(method) && formData) {
		options.body = formData;
	}

	try {
		// Execute pre-request callback
		if (onRequestStart) onRequestStart();
		
		// Make the fetch request
		const response = await fetch(url, options);
		const responseData = await response.json();
		const { success, data } = responseData;
		
		// Handle non-ok responses
		if (!response.ok) throw new Error(data?.message || 'Network response was not ok');
		
		// Execute success callback if response is successful
		if (success && onSuccess) onSuccess(data);
	} catch (error) {
		// Execute error callback
		if (onError) onError(error);
	} finally {
		// Execute finally callback regardless of success/failure
		if (onFinally) onFinally();
	}
};